<template>
  <v-card v-if="employee && employee.id">
    <v-card-title>
      <span>{{ employee | name }} {{ employee.id }}</span>
      <span class="text--disabled">{{ employee.employeeId | idBrackets }}</span>
      <v-spacer />
      <employee-impersonate :id="employee.id" />

      <employee-password-reset :id="employee.id" />

      <employee-password-change :id="employee.id" />

      <employee-deactivate :employee="employee" @refresh="$emit('refresh')" />

      <change-employee-accounts
        :id="employee.id"
        :oldAccounts="employee.oldAccounts"
        @refresh="$emit('refresh')"
      />
      <employee-personal-information
        :id="employee.id"
        @refresh="$emit('refresh')"
      />

      <employee-toggle-history :state.sync="showHistory" />
    </v-card-title>

    <v-card-subtitle>
      <div v-if="!employee.isActive">{{ $t("import.inactive") }}</div>

      <span v-if="employee.started !== '1970-01-01T00:00:00Z'">
        {{ $t("employees.column.started") }} {{ employee.started | date }}
        {{ $t("employees.column.added") }} {{ employee.created | date }}
      </span>
      <span v-else
        >{{ $t("employees.manuallyImported") }}
        {{ employee.created | date }}</span
      >
    </v-card-subtitle>

    <v-card-text>
      <v-row>
        <v-col cols="12" lg="6">
          <data-row :tooltip="$t('tags.location')" icon="location_on">
            <template #content>
              <employee-tags :tags="getCategoryTags(employee, 'location')" />
            </template>
            <template #actions></template>
          </data-row>

          <data-row :tooltip="$t('employees.column.role')" icon="contacts">
            <template #content>{{ $t("role." + employee.role) }}</template>
            <template #actions>
              <employee-role :id="employee.id" :role.sync="employee.role" />
            </template>
          </data-row>

          <data-row :tooltip="$t('tags.department')" icon="home_work">
            <template #content>
              <employee-tags :tags="getCategoryTags(employee, 'department')" />
            </template>
            <template #actions>
              <employee-category-tags
                :id="employee.id"
                :tags.sync="employee.tags"
                @refresh="$emit('refresh')"
                category="department"
                :multiple="true"
                name="changeDepartments"
                :importSettings="true"
                :role="
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  )
                "
              />
            </template>
          </data-row>

          <data-row :tooltip="$t('tags.position')" icon="build">
            <template #content>
              <employee-tags :tags="getCategoryTags(employee, 'position')" />
            </template>
            <template #actions>
              <employee-positions
                :id="employee.id"
                :employeeTags="employee.tags"
                @refresh="$emit('refresh')"
              />
            </template>
          </data-row>

          <data-row :tooltip="$t('profile.manager')" icon="supervisor_account">
            <template #content>{{ employee.manager | nameId }}</template>
            <template #actions>
              <change-manager
                :id="employee.id"
                :manager="employee.manager"
                @manager_changed="(m) => (employee.manager = m)"
              />
            </template>
          </data-row>
        </v-col>
        <v-col cols="12" lg="6">
          <data-row
            :tooltip="$t('tags.shift')"
            icon="access_time"
            v-if="getCategoryTags(employee, 'shift')"
          >
            <template #content>
              <employee-tags :tags="getCategoryTags(employee, 'shift')" />
            </template>
            <template #actions>
              <employee-category-tags
                :id="employee.id"
                :tags.sync="employee.tags"
                @refresh="$emit('refresh')"
                category="shift"
                :multiple="false"
                name="changeShift"
                :importSettings="false"
                :role="
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  )
                "
              />
            </template>
          </data-row>

          <data-row
            :tooltip="$t('tags.collar')"
            icon="person_add"
            v-if="getCategoryTags(employee, 'collar')"
          >
            <template #content>
              <employee-tags :tags="getCategoryTags(employee, 'collar')" />
            </template>
            <template #actions>
              <employee-category-tags
                :id="employee.id"
                :tags.sync="employee.tags"
                @refresh="$emit('refresh')"
                category="collar"
                :multiple="true"
                name="changeCollar"
                :importSettings="false"
                :role="
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  )
                "
              />
            </template>
          </data-row>

          <data-row :tooltip="$t('profile.email')" icon="email">
            <template #content>{{ employee.email }}</template>
            <template #actions>
              <notification-settings-dialog
                :id="id"
                :email="employee.email"
                :subscriptions="employee.subscriptions"
                @refresh="$emit('refresh')"
              />
            </template>
          </data-row>

          <data-row :tooltip="$t('instructions.column.modes')" icon="school">
            <template #content>{{
              employee.modes && employee.modes.map((m) => m.name).join(", ")
            }}</template>
            <template #actions>
              <employee-modes
                :id="employee.id"
                :modes.sync="employee.modes"
                @refresh="$emit('refresh')"
              />
            </template>
          </data-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import EmployeeTags from "@/components/employee/EmployeeTags.vue";
import DataRow from "@/components/employee/EmployeeProfileDataRow.vue";
import EmployeeRole from "@/components/EmployeeRole";
import EmployeeCategoryTags from "@/components/EmployeeCategoryTags";
import ChangeManager from "@/components/control/ChangeManager.vue";
import EmployeeImpersonate from "@/components/EmployeeImpersonate";
import EmployeePositions from "@/components/EmployeePositions";
import EmployeePersonalInformation from "@/components/EmployeePersonalInformation";
import EmployeePasswordReset from "@/components/EmployeePasswordReset";
import EmployeePasswordChange from "@/components/EmployeePasswordChange";
import EmployeeDeactivate from "@/components/control/EmployeeDeactivate";
import EmployeeModes from "@/components/EmployeeModes";
import ChangeEmployeeAccounts from "@/components/employee/ChangeEmployeeAccounts";
import EmployeeToggleHistory from "@/components/control/EmployeeToggleHistory";
import NotificationSettingsDialog from "@/components/notification/NotificationSettingsDialog.vue";

export default {
  props: {
    id: {
      type: Number,
      required: true,
    },
    employee: {
      type: Object,
      required: true,
    },
  },
  components: {
    EmployeeTags,
    DataRow,
    EmployeeRole,
    EmployeeCategoryTags,
    ChangeManager,
    EmployeeImpersonate,
    EmployeePositions,
    EmployeePersonalInformation,
    EmployeePasswordReset,
    EmployeePasswordChange,
    EmployeeDeactivate,
    EmployeeModes,
    ChangeEmployeeAccounts,
    EmployeeToggleHistory,
    NotificationSettingsDialog,
  },
  data() {
    return {
      showHistory: false,
    };
  },
  computed: {
    ...mapGetters(["role"]),
    ...mapState(["me"]),
  },
  watch: {
    showHistory(val) {
      this.$emit("showHistoryChange", val);
    },
  },
  methods: {
    getCategoryTags(employee, category) {
      return (
        (employee.tags &&
          employee.tags.filter((t) => t.category == category)) ||
        []
      );
    },
  },
};
</script>
